/**
 * Static guidance for "Comparison at a glance" scorecards — causes & Datadog checks per metric.
 * Tone comes from verdict: 'good' | 'bad' | 'neutral' (Azure vs Colo for this metric).
 */

const COMMON_DATADOG = [
  'Filter APM by the same service and env tags used in this view.',
  'Compare trace samples from the same calendar week if traffic patterns differ between envs.'
];

export const METRIC_DEEP_DIVES = {
  http: {
    title: 'Outbound HTTP client latency',
    whatItMeasures:
      'Time spent in outbound HTTP calls from the service (dependencies, APIs, auth, other microservices). Often network- and dependency-bound.',
    whenAzureWorse: [
      'Different network path: egress, DNS, TLS, proxy, or region vs dependencies.',
      'Dependency moved or throttled; higher latency to DB, identity, or partner APIs from Azure region.',
      'Connection pool exhaustion or new retry/backoff behavior.'
    ],
    whenAzureBetter: [
      'Closer network path to dependencies from Azure region, or peering improvements.',
      'Dependency upgraded or scaled on the other side.'
    ],
    datadogChecks: [
      'APM → Trace search: filter errors/slow spans on outbound HTTP.',
      'Service map: edge from this service to downstreams; compare latency by peer service.',
      'Network / DNS dashboards if you tag outbound calls.',
      ...COMMON_DATADOG
    ]
  },
  err: {
    title: 'HTTP error rate',
    whatItMeasures:
      'Share of traced HTTP requests reporting errors (application or dependency failures). Higher is worse.',
    whenAzureWorse: [
      'Config/secrets mismatch in Azure (wrong endpoints, certificates, feature flags).',
      'Stricter validation or auth (tokens, cookies) behaving differently across envs.',
      'Timeouts or 5xx from dependencies under Azure load or regional routing.',
      'Data migration edge cases (encoding, IDs, permissions).'
    ],
    whenAzureBetter: [
      'Fixes deployed only to Azure, or reduced noise after stabilization.',
      'Different traffic mix during ramp — confirm absolute error counts, not only rates.'
    ],
    datadogChecks: [
      'APM → Traces filtered by error=true; group by resource_name and http.status_code.',
      'Logs for the same service/env around error spikes; correlate with deploys.',
      'Compare top errors tables below this section for Colo vs Azure.',
      ...COMMON_DATADOG
    ]
  },
  p99: {
    title: 'Inbound latency — p99 (worst-case)',
    whatItMeasures:
      '99th-percentile inbound servlet latency — the slowest 1% of requests. Highly sensitive to individual slow traces, but the best early-warning signal for GC, dependency timeouts, and saturation that averages can hide. Pair with the Apache access-log avg (real-user, edge-measured) to tell in-JVM contention apart from edge-side issues.',
    whenAzureWorse: [
      'Full GC pauses, long-running DB queries, dependency timeouts, or retry storms.',
      'Thread-pool queueing: a spike in concurrent requests pushes the slowest ones off a cliff.',
      'Network hiccups on a subset of requests (egress path, DNS, TLS renegotiation).'
    ],
    whenAzureBetter: [
      'Worst-case pathologies addressed (e.g. shorter dependency timeout, removed full-table scan).',
      'Lower concurrency per instance — verify with CPU and error rate before crediting the move.'
    ],
    datadogChecks: [
      'APM → open the individual slowest traces in the worst week; look for a single span that dominates duration.',
      'Check logs for OOM / full GC / dependency timeout events aligned with the spike.',
      'Compare p99 against error rate — a p99 spike that coincides with a 5xx spike usually points to a dependency timeout rather than app code.',
      ...COMMON_DATADOG
    ]
  },
  cpu: {
    title: 'Host CPU (user)',
    whatItMeasures:
      'Average CPU time in user space on hosts tagged with the env. Reflects app + agent work on the box; not pure app-only CPU.',
    whenAzureWorse: [
      'Undersized VM / App Service plan vs previous Colo capacity.',
      'Inefficient hot paths, logging volume, or polling loops.',
      'More co-tenancy or noisy neighbor on shared hosts.'
    ],
    whenAzureBetter: [
      'Larger SKU or fewer competing processes; or lower request load on Azure.',
      'If the instance is idle, CPU can look "better" without being healthier — cross-check with inbound latency and error rate.'
    ],
    datadogChecks: [
      'Infrastructure list: CPU by host for this env; top processes if available.',
      'APM profiling or CPU flame graphs if enabled for the service.',
      'Compare with memory and latency — CPU alone does not tell the whole story.',
      ...COMMON_DATADOG
    ]
  },
  apacheApi: {
    title: 'Apache API avg (real user response time)',
    whatItMeasures:
      'Average response time for API traffic (`@call:api`) measured at the Apache access log — includes network, TLS, and the Apache proxy hop, so this is the closest thing to what the calling service or SDK actually experiences. Complements APM `trace.servlet.request` (which starts inside the servlet and therefore hides edge-side regressions).',
    whenAzureWorse: [
      'Azure Front Door / Application Gateway / Load Balancer adds latency on the edge path (TLS termination, buffering, routing).',
      'Network hop from caller to Azure region is longer than Colo (DNS, peering, cross-region egress).',
      'Apache keep-alive reuse dropped — each API call pays a new TCP + TLS handshake.',
      'Proxy / `mod_proxy` tuning differs (buffer sizes, timeouts, worker count).',
      'If APM `trace.servlet.request` looks fine but Apache avg is worse, the extra time is outside the app.'
    ],
    whenAzureBetter: [
      'Closer network path or better peering from the caller to the Azure region.',
      'Apache / proxy tuning on Azure (larger buffer, tighter keep-alive) reduces edge overhead.',
      'Smaller payloads or fewer redirects after cutover.'
    ],
    datadogChecks: [
      'Log Explorer: `source:apache @HQApacheInstanceName:<client> @call:api` — compare `@ApacheDuration` across envs, then subtract APM `trace.servlet.request` to isolate edge overhead.',
      'Check `@http.status_code` mix — a rise in 499 / 502 / 504 alongside higher avg points to proxy or upstream timeout issues.',
      'Correlate spikes with Azure Front Door / Application Gateway metrics and network path changes.',
      ...COMMON_DATADOG
    ]
  },
  apacheApiP95: {
    title: 'Apache API p95 (tail response time)',
    whatItMeasures:
      '95th-percentile API response time at the Apache access log — the slowest 5% of real API calls. More sensitive than the average to spikes, slow paths, and transient network issues. An elevated p95 while avg looks fine usually means a subset of calls hit a slow dependency, a GC pause, or a longer edge-side route.',
    whenAzureWorse: [
      'Sporadic TCP retransmits or TLS renegotiations on the Azure edge path pushing the tail up.',
      'Timeout or retry jitter from a dependency (DB, identity, external API) that affects a small percentage of calls.',
      'Thread-pool or connection-pool saturation under burst traffic — most calls are fast, the tail queues.'
    ],
    whenAzureBetter: [
      'Fewer slow paths due to infrastructure improvements (faster dependency routing, bigger thread pool).',
      'Reduced retry/backoff storms after migration stabilization.'
    ],
    datadogChecks: [
      'Log Explorer: `source:apache @HQApacheInstanceName:<client> @call:api` — sort by `@ApacheDuration` descending to inspect the slowest individual requests.',
      'APM slow traces aligned to the same time window — filter by resource name for the API paths that appear in the slow-log list.',
      'Check for "Requests > 2s" and "Requests > 3s" counts on the Apache dashboard to understand the shape of the tail.',
      ...COMMON_DATADOG
    ]
  },
  apacheApiP99: {
    title: 'Apache API p99 (worst-case response time)',
    whatItMeasures:
      '99th-percentile API response time at the Apache access log — the slowest 1% of real API calls as seen at the server edge. Highly sensitive to individual pathological requests. Pair with APM p99 to distinguish in-app latency from edge/proxy overhead.',
    whenAzureWorse: [
      'Individual extremely slow requests (full-table scan, large payload, lock contention) are amplified at p99.',
      'Azure edge overhead on long-running requests (proxy buffers, connection reuse).',
      'Rare dependency timeouts that always hit the same endpoint category.'
    ],
    whenAzureBetter: [
      'Worst-case bottlenecks resolved (dependency timeout reduced, hot query optimized).',
      'Traffic ramp-up on Azure means fewer truly concurrent long requests hitting the worst case simultaneously.'
    ],
    datadogChecks: [
      'Log Explorer: sort `@ApacheDuration` desc on `source:apache @call:api` — look for patterns in URL, status code, or time of day.',
      'APM: open individual traces for the slowest API requests; the top span usually identifies the root cause.',
      'Cross-check with error rate — p99 spikes that coincide with 5xx spikes typically point to dependency timeouts.',
      ...COMMON_DATADOG
    ]
  },
  apacheUi: {
    title: 'Apache UI avg (real user response time)',
    whatItMeasures:
      'Average response time for browser traffic (`-@call:api`) measured at the Apache access log. Closer to "page feels slow" than APM trace duration because it includes TLS, proxy and asset-delivery hops a servlet trace cannot see.',
    whenAzureWorse: [
      'TLS or CDN changes affecting asset delivery for browser sessions.',
      'Geography: UI clients are scattered worldwide — Azure region may be farther for some regions, closer for others.',
      'Session / cookie behavior differs (auth redirects, CORS preflights hitting the server).',
      'More static assets served from the origin instead of a CDN edge after cutover.'
    ],
    whenAzureBetter: [
      'Better TLS termination or newer protocol (HTTP/2, HTTP/3) enabled on Azure edge.',
      'Users close to the Azure region see lower RTT than they did for the Colo origin.',
      'Smaller or better-cached assets after cutover.'
    ],
    datadogChecks: [
      'Log Explorer: `source:apache @HQApacheInstanceName:<client> -@call:api` — compare `@ApacheDuration` for browser traffic.',
      'Group by `@http.url_details.path` to see which pages regressed most; heavy pages (file lists, dashboards) are the usual suspects.',
      'If RUM is enabled, cross-check with page-load distribution to confirm what users feel.',
      ...COMMON_DATADOG
    ]
  },
  apacheUiP95: {
    title: 'Apache UI p95 (tail browser response time)',
    whatItMeasures:
      '95th-percentile response time for browser traffic at the Apache access log. Captures the slow end of the user experience — the sessions that wait longest for pages to load. Elevated p95 UI latency often signals specific heavy pages, CDN misses, or auth redirect chains affecting a subset of sessions.',
    whenAzureWorse: [
      'Heavy pages (file browsers, dashboards) served from origin rather than CDN after cutover.',
      'TLS session reuse rate lower on Azure — some browsers pay a full handshake per page load.',
      'A subset of users on longer RTT paths to the Azure region (geography change).'
    ],
    whenAzureBetter: [
      'Better TLS or HTTP/2 support on Azure edge reduces handshake overhead for the slow sessions.',
      'Fewer auth redirects or CSRF preflight requests after cutover.'
    ],
    datadogChecks: [
      'Log Explorer: `source:apache @HQApacheInstanceName:<client> -@call:api` — group by `@HQApacheRequestUrl` and sort by p95 to find which pages are slow.',
      'Check "UI Requests > 2s" and "UI Requests > 3s" on the Apache dashboard for volume context.',
      ...COMMON_DATADOG
    ]
  },
  apacheUiP99: {
    title: 'Apache UI p99 (worst-case browser response time)',
    whatItMeasures:
      '99th-percentile response time for browser traffic at the Apache access log — the slowest 1% of page loads as measured at the server. A useful proxy for the worst user experience complaints. High p99 UI latency often relates to large assets, sync render-blocking requests, or the occasional session that hits a cold cache.',
    whenAzureWorse: [
      'Cold-cache misses on Azure for large files or session state — some sessions wait for the full round-trip.',
      'Certain URL paths that are rarely hit but take long (export, report generation) skew p99 heavily.',
      'User sessions from distant regions seeing full TCP + TLS handshakes on each request.'
    ],
    whenAzureBetter: [
      'Long-running UI operations (bulk exports, search indexing) were offloaded or cached.',
      'Fewer slow auth flows after migration consolidation.'
    ],
    datadogChecks: [
      'Log Explorer: sort `@ApacheDuration` desc on `source:apache -@call:api` — the slowest entries reveal the page type and frequency.',
      'If RUM is enabled, correlate with LCP or FCP to confirm the slowest backend responses map to user-visible page slowness.',
      ...COMMON_DATADOG
    ]
  },
  mem: {
    title: 'Memory (% usable)',
    whatItMeasures:
      'Host-level usable memory (Datadog system.mem.pct_usable). Higher usually means more free headroom on the box.',
    whenAzureWorse: [
      'Tighter heap/container limits or smaller RAM on Azure SKU.',
      'Memory leaks or higher heap usage after migration.',
      'More processes on the same host.'
    ],
    whenAzureBetter: [
      'Larger RAM allocation or lower pressure after tuning.',
      'Lower concurrency on Azure can also increase headroom — cross-check against inbound p99 and error rate before crediting the move.'
    ],
    datadogChecks: [
      'Host memory graphs; JVM heap used vs max if you have process-level metrics.',
      'GC and OOM-related logs for the app tier.',
      ...COMMON_DATADOG
    ]
  }
};

/**
 * @param {string} key - scorecard key (p99 | http | err | cpu | mem | apacheApi | apacheApiP95 | apacheApiP99 | apacheUi | apacheUiP95 | apacheUiP99)
 * @param {{ verdict: { tone: string }, lowerBetter?: boolean }} item
 */
export function getDeepDiveSections(key, item) {
  const d = METRIC_DEEP_DIVES[key];
  if (!d) return null;
  const tone = item?.verdict?.tone || 'neutral';

  let focusLabel = 'What to explore';
  let focusBullets = [];

  if (tone === 'bad') {
    focusLabel = 'Common causes for this gap (Azure vs Colo)';
    focusBullets = d.whenAzureWorse;
  } else if (tone === 'good') {
    focusLabel = 'What might explain the improvement (verify)';
    focusBullets = d.whenAzureBetter;
  } else {
    focusLabel = 'Context to consider';
    focusBullets = [...d.whenAzureWorse.slice(0, 2), ...d.whenAzureBetter.slice(0, 2)];
  }

  return {
    ...d,
    focusLabel,
    focusBullets,
    tone
  };
}
